<script lang="ts">
  import type { Snippet } from 'svelte';
  import type { DashboardRole } from '../../lib/dashboard/resolve';

  let {
    title,
    subtitle = '',
    variant = 'admin' as DashboardRole | 'admin',
    children,
    actions,
  }: {
    title: string;
    subtitle?: string;
    variant?: DashboardRole | 'admin';
    children: Snippet;
    actions?: Snippet;
  } = $props();
</script>

<div class="dash dash-{variant}">
  <header class="head">
    <div class="head-left">
      <span class="role-tag">{title}</span>
      <h2>{title}</h2>
      {#if subtitle}<p>{subtitle}</p>{/if}
    </div>
    {#if actions}<div class="head-actions">{@render actions()}</div>{/if}
  </header>
  {@render children()}
</div>

<style>
  .dash {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding: 0.25rem;
    border-radius: var(--radius-2xl);
  }

  .head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
    padding: 0.85rem 1.15rem;
    background: var(--glass-bg-strong);
    backdrop-filter: var(--glass-blur);
    -webkit-backdrop-filter: var(--glass-blur);
    border: 1px solid var(--glass-border);
    border-radius: var(--radius-xl);
    box-shadow: var(--glass-shadow);
  }

  .head-left {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .role-tag {
    display: inline-block;
    font-size: 0.62rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    margin-bottom: 0.15rem;
    width: fit-content;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.3);
  }

  h2 {
    margin: 0;
    font-size: 1.35rem;
    color: var(--text-primary);
    letter-spacing: -0.02em;
    font-weight: 800;
  }

  .head p {
    margin: 0.15rem 0 0;
    font-size: 0.82rem;
    color: var(--text-secondary);
  }

  .head-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  /* Role tag colors — vibrant glass pills */
  .dash-admin .role-tag       { background: linear-gradient(135deg, #005f73, #0096c7); color: #fff; box-shadow: 0 2px 12px rgba(0,150,199,0.3); }
  .dash-enfermeria .role-tag  { background: linear-gradient(135deg, #1e3a5f, #3b82f6); color: #fff; box-shadow: 0 2px 12px rgba(59,130,246,0.3); }
  .dash-medico .role-tag      { background: linear-gradient(135deg, #0f766e, #14b8a6); color: #fff; box-shadow: 0 2px 12px rgba(20,184,166,0.3); }
  .dash-recepcion .role-tag   { background: linear-gradient(135deg, #92400e, #f59e0b); color: #fff; box-shadow: 0 2px 12px rgba(245,158,11,0.3); }
  .dash-farmacia .role-tag    { background: linear-gradient(135deg, #5b21b6, #8b5cf6); color: #fff; box-shadow: 0 2px 12px rgba(139,92,246,0.3); }
  .dash-laboratorio .role-tag { background: linear-gradient(135deg, #3730a3, #6366f1); color: #fff; box-shadow: 0 2px 12px rgba(99,102,241,0.3); }
  .dash-imagenologia .role-tag{ background: linear-gradient(135deg, #0e7490, #22d3ee); color: #fff; box-shadow: 0 2px 12px rgba(34,211,238,0.3); }
  .dash-quirofano .role-tag   { background: linear-gradient(135deg, #9f1239, #e11d48); color: #fff; box-shadow: 0 2px 12px rgba(225,29,72,0.3); }
  .dash-caja .role-tag        { background: linear-gradient(135deg, #166534, #22c55e); color: #fff; box-shadow: 0 2px 12px rgba(34,197,94,0.3); }
  .dash-finanzas .role-tag    { background: linear-gradient(135deg, #1e3a8a, #3b82f6); color: #fff; box-shadow: 0 2px 12px rgba(59,130,246,0.3); }
  .dash-mspas .role-tag       { background: linear-gradient(135deg, #374151, #6b7280); color: #fff; box-shadow: 0 2px 12px rgba(107,114,128,0.3); }
  .dash-auditoria .role-tag   { background: linear-gradient(135deg, #1f2937, #4b5563); color: #fff; box-shadow: 0 2px 12px rgba(75,85,99,0.3); }

  /* Grid system */
  :global(.dash-grid) {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 0.85rem;
  }
  :global(.col-3) { grid-column: span 3; }
  :global(.col-4) { grid-column: span 4; }
  :global(.col-6) { grid-column: span 6; }
  :global(.col-8) { grid-column: span 8; }
  :global(.col-12) { grid-column: span 12; }

  @media (max-width: 960px) {
    :global(.col-3), :global(.col-4), :global(.col-6), :global(.col-8) { grid-column: span 12; }
  }
</style>
